// Read-only views of already scoped Sessions / count-ledger records. No storage,
// cloud requests, Response Intelligence or stock mutations belong in this module.
import { buildSessionReviewHref, getSessionOperationalDate } from './mcSessionDateMemory.js';

export const SESSION_HISTORY_PERIODS = [
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: 'all', label: 'All time', days: null },
];

const text = (value, fallback = '') => String(value ?? '').trim() || fallback;
const number = (value) => value === null || value === undefined || value === '' || typeof value === 'boolean'
  ? null : Number.isFinite(Number(value)) ? Number(value) : null;
const round = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const timestamp = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};
const dayKey = (time) => {
  const parts = new Intl.DateTimeFormat('en', { timeZone: 'Africa/Johannesburg', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(time);
  return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type).value).join('-');
};

export function sessionDashboardRow(record = {}, now = Date.now()) {
  now = timestamp(now) ?? Date.now();
  const meta = { ...record.detailSnapshot?.meta, ...record.meta };
  const id = text(record.sessionId || record.sourceSessionId);
  const rawStatus = text(record.status || meta.status).toLowerCase();
  const archived = record.dashboardSource === 'archived' || record.archived === true || Boolean(record.archivedAt || record.archiveType || record.archiveReason)
    || ['archived', 'archive'].includes(rawStatus);
  const archiveType = text(record.archiveType || record.archiveReason || meta.archiveType).toLowerCase();
  const loading = !archived && record.dashboardLedgerReady === false;
  const total = loading ? null : number(record.totalLines ?? record.finalTotalLines ?? meta.totalLines);
  const done = loading ? null : number(record.completedLines ?? record.finalCompletedLines ?? meta.completedLines);
  const totalLines = total === null ? null : Math.max(0, total);
  const completedLines = done === null ? null : Math.max(0, done);
  const fullyCounted = totalLines > 0 && completedLines !== null && completedLines >= totalLines;
  const sectionComplete = !loading && Boolean(record.sectionCompletedAt || record.completedAt || meta.sectionCompletedAt || meta.completedAt
    || ['section complete', 'section-complete', 'completed'].includes(rawStatus));
  const verified = !loading && Boolean(record.verifiedAt || record.verification?.verifiedAt || meta.verifiedAt || meta.verification?.verifiedAt
    || text(record.verificationStatus || record.verification?.status || meta.verificationStatus || meta.verification?.status).toLowerCase() === 'verified');
  const finalized = Boolean(record.finalizedAt || meta.finalizedAt
    || text(record.lifecycleState || meta.lifecycleState).toLowerCase() === 'finalized' || rawStatus === 'finalized');
  const historicalComplete = archived && (rawStatus === 'completed' || archiveType === 'completed' || archiveType.startsWith('finalized') || fullyCounted || finalized);
  const updatedTime = timestamp(archived ? record.archivedAt || record.updatedAt || meta.updatedAt || record.startedAt || record.createdAt
    : record.updatedAt || meta.lastEditedAt || record.startedAt || record.createdAt);
  const isEarlierCount = !archived && !loading && updatedTime !== null && dayKey(updatedTime) < dayKey(now);
  let stage;
  if (loading) stage = 'loading';
  else if (historicalComplete || finalized) stage = 'completed';
  else if (archived) stage = 'unfinished';
  else if (sectionComplete) stage = verified ? 'finalize' : 'verification';
  else if (fullyCounted) stage = 'complete-section';
  else if (isEarlierCount) stage = 'unfinished';
  else if (!totalLines) stage = 'preparing';
  else if (!completedLines) stage = 'ready';
  else stage = 'counting';
  const status = {
    loading: 'Loading count details', completed: 'Completed', unfinished: archived ? 'Archived incomplete' : 'Unfinished count',
    finalize: 'Ready to finalize', verification: 'Awaiting verification', 'complete-section': 'Ready to complete',
    preparing: 'Preparing', ready: 'Ready to count', counting: 'Counting',
  }[stage];
  const action = { verification: 'Verify count', finalize: 'Finalize count', 'complete-section': 'Complete count', unfinished: archived ? 'View record' : 'Continue count' }[stage] || 'Open count';
  const variance = loading || record.dashboardVarianceKnown === false ? null : number(record.costExposure ?? record.finalExposure ?? meta.costExposure ?? meta.finalExposure);
  const href = id ? `/morpheus-count/session/${encodeURIComponent(id)}${archived ? '?archived=1' : ''}` : '/morpheus-count/sessions';
  const operationalRecord = { ...record, meta };
  return {
    id, site: text(record.dashboardSiteLabel || record.storeName || record.store || record.siteName, 'Unknown site'),
    cycle: text(record.cycleName || meta.cycleName || record.cycle, 'Count cycle'),
    section: text(record.sectionName || meta.sectionName || record.section, 'Full count'),
    owner: text(record.startedBy || record.ownerName || meta.startedBy || meta.ownerName, 'Unassigned'),
    stage, status, archived, verified, sectionComplete: sectionComplete || historicalComplete || finalized,
    totalLines, completedLines,
    progress: totalLines > 0 && completedLines !== null ? Math.max(0, Math.min(100, Math.round(completedLines / totalLines * 100))) : null,
    variance, flags: loading ? null : number(record.flaggedCount ?? record.finalFlagCount ?? meta.flaggedCount),
    updatedAt: updatedTime === null ? '' : new Date(updatedTime).toISOString(), updatedTime,
    operationalDate: getSessionOperationalDate(operationalRecord), href,
    actionHref: ['verification', 'finalize'].includes(stage) ? buildSessionReviewHref(operationalRecord) : href, action,
  };
}

// Resolve presentation from the capabilities already used by Sessions. A count
// can stay visible to a staff profile without offering a privileged action.
export function sessionDashboardAction(row = {}, permissions = {}) {
  const restricted = (row.stage === 'verification' && permissions?.canVerify !== true)
    || (row.stage === 'finalize' && permissions?.canFinalize !== true);
  return {
    label: restricted ? 'View count' : (row.action || 'View count'),
    href: restricted ? row.href : (row.actionHref || row.href),
  };
}

export function buildSessionDashboard(records = [], options = {}) {
  const now = timestamp(options.now) ?? Date.now();
  const seen = new Set();
  // Archives win over stale active discovery rows, matching Sessions.
  const ordered = [...(Array.isArray(records) ? records : [])].filter((record) => record && !record.deletedAt && !record.meta?.deletedAt
    && text(record.status || record.meta?.status).toLowerCase() !== 'deleted').sort((a, b) =>
    Number(b.dashboardSource === 'archived' || b.archived === true || Boolean(b.archivedAt)) - Number(a.dashboardSource === 'archived' || a.archived === true || Boolean(a.archivedAt)));
  const rows = ordered.filter((record) => {
    const id = text(record.sessionId || record.sourceSessionId);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  }).map((record) => sessionDashboardRow(record, now)).sort((a, b) => (b.updatedTime ?? 0) - (a.updatedTime ?? 0));
  // Archives belong to history. They are never outstanding work in Sessions,
  // even when they were archived before the count was finished.
  const activeRows = rows.filter((row) => !row.archived && row.stage !== 'completed');
  const awaitingVerification = activeRows.filter((row) => row.stage === 'verification');
  const knownVerification = awaitingVerification.filter((row) => row.variance !== null);
  // Keep the same newest-first order in every dashboard list.
  const attention = activeRows.filter((row) => ['verification', 'finalize', 'complete-section', 'unfinished'].includes(row.stage));
  // An earlier unfinished count is still open work. 100% progress also remains
  // here until the operator explicitly completes the section in Sessions.
  const inProgress = activeRows.filter((row) => !row.sectionComplete);
  const latestTime = activeRows.filter((row) => row.stage !== 'loading' && row.updatedTime !== null).reduce((latest, row) => Math.max(latest, row.updatedTime), 0);
  return {
    scope: text(options.scope, 'All Sites'), rows, activeRows, attention, inProgress, awaitingVerification,
    verificationVariance: awaitingVerification.length && !knownVerification.length ? null : round(knownVerification.reduce((sum, row) => sum + row.variance, 0)),
    missingVerificationValues: awaitingVerification.length - knownVerification.length,
    unfinishedCount: activeRows.filter((row) => row.stage === 'unfinished').length,
    readyToFinalizeCount: activeRows.filter((row) => row.stage === 'finalize').length,
    loadingCount: activeRows.filter((row) => row.stage === 'loading').length,
    completedCount: rows.filter((row) => row.sectionComplete).length,
    latestAt: latestTime ? new Date(latestTime).toISOString() : '',
    hasData: activeRows.length > 0, hasHistoryData: rows.length > 0,
  };
}

export function buildSessionHistory(dashboard, options = {}) {
  const period = SESSION_HISTORY_PERIODS.find((item) => item.id === options.period) || SESSION_HISTORY_PERIODS[0];
  const now = timestamp(options.now) ?? Date.now();
  const cutoff = period.days ? now - period.days * 86400000 : null;
  const status = ['all', 'completed', 'unfinished'].includes(options.status) ? options.status : 'all';
  const rows = dashboard.rows.filter((row) => (cutoff === null || row.updatedTime !== null && row.updatedTime >= cutoff)
    && (status === 'all' || (status === 'completed' ? row.sectionComplete : !row.sectionComplete)));
  return {
    period, status, rows, count: rows.length,
    latestAt: rows.find((row) => row.stage !== 'loading' && row.updatedTime !== null)?.updatedAt || '',
    completedCount: rows.filter((row) => row.sectionComplete).length,
    // A trend point is one completed count, never a sum of repeated stock counts.
    trend: rows.filter((row) => row.sectionComplete && row.variance !== null).slice(0, 10),
  };
}

export function sessionDashboardMoney(value) {
  if (number(value) === null) return 'Not available';
  return `${value < 0 ? '-' : ''}R ${Math.abs(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function sessionDashboardDate(value, withTime = true) {
  const time = timestamp(value);
  return time === null ? 'Not recorded' : new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Johannesburg', day: '2-digit', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } : {}),
  }).format(time) + (withTime ? ' SAST' : '');
}
